import { useImperativeHandle, useMemo } from 'react'

import {
  DownloadOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SwapOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from '@ant-design/icons'
import { useSetState } from 'ahooks'
import { Image, Space } from 'antd'
import { createStyles } from 'antd-style'

import { toArray } from '../../utils/array'
import { downloadUrl } from '../../utils/filesaver'

import { PreviewFileProps, PreviewItem } from './interface'

const useStyles = createStyles(({ token }) => ({
  toolbar: {
    padding: `0 ${token.padding}px`,
    fontSize: token.fontSizeLG,
    backgroundColor: `rgba(0, 0, 0, 0.1)`,
    borderRadius: 100,
    ['& .anticon']: {
      padding: token.padding,
      fontSize: token.fontSizeXL,
      cursor: 'pointer'
    },
    ['& .anticon[disabled]']: {
      opacity: 0.3,
      cursor: 'not-allowed'
    },
    ['& .anticon:hover']: {
      opacity: 0.3
    }
  }
}))

export const PreviewImage = ({ ref }: PreviewFileProps) => {
  const [{ current, visible, fileList }, setState] = useSetState({
    current: 0,
    visible: false,
    fileList: [] as PreviewItem[]
  })

  const items = useMemo(() => fileList.map(file => `${file.url}`), [fileList])

  useImperativeHandle(ref, () => {
    return {
      open(current, files) {
        const fileList = files ?? toArray(current)
        const index = Math.max(
          0,
          fileList.findIndex(item => item.uid === current.uid)
        )
        setState({
          current: index,
          visible: true,
          fileList
        })
      }
    }
  })

  const handleCurrentChange = (index: number) => {
    setState({
      current: index
    })
  }

  const handleVisibleChange = (visible: boolean) => {
    setState({
      visible
    })
  }

  const handleDownload = () => {
    if (current === undefined) {
      return
    }

    const file = fileList[current]

    downloadUrl(`${file.url}`, file.name)
  }

  const { styles } = useStyles()

  return (
    <Image.PreviewGroup
      items={items}
      preview={{
        current,
        open: visible,
        onOpenChange: handleVisibleChange,
        onChange: handleCurrentChange,
        actionsRender: (
          _,
          {
            transform: { scale },
            actions: {
              onFlipY,
              onFlipX,
              onRotateLeft,
              onRotateRight,
              onZoomOut,
              onZoomIn
            }
          }
        ) => (
          <Space className={styles.toolbar} size={12}>
            <SwapOutlined rotate={90} title='上下翻转' onClick={onFlipY} />
            <SwapOutlined title='左右翻转' onClick={onFlipX} />
            <RotateLeftOutlined title='向左旋转' onClick={onRotateLeft} />
            <RotateRightOutlined title='向右旋转' onClick={onRotateRight} />
            <ZoomOutOutlined
              disabled={scale === 1}
              title='缩小'
              onClick={onZoomOut}
            />
            <ZoomInOutlined
              disabled={scale === 50}
              title='放大'
              onClick={onZoomIn}
            />
            <DownloadOutlined title='下载' onClick={handleDownload} />
          </Space>
        )
      }}
    />
  )
}
